import React from "react";
import { Box, Typography, Switch, TextField } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { themes } from "services/constants";
import { boxStyle, mainOptions } from "../ShiftCategoryOptions";

const thresholdCountRegex = /^\d{0,8}(\.\d{0,2})?$/;
const penaltyValueRegex = /^\d{0,5}(\.\d{0,2})?$/;

export const LeaveToAbsentConversion = ({
  watch,
  setValue,
  register,
  errors,
}) => (
  <Box sx={mainOptions}>
    <Box sx={boxStyle(watch("is_leave_to_absent_convert"))}>
      <Typography fontWeight="medium">Leave / Absence Penalty</Typography>
      <Switch
        checked={watch("is_leave_to_absent_convert")}
        onChange={(event) => {
          const checked = event.target.checked;
          setValue("is_leave_to_absent_convert", checked);
          if (!checked) {
            setValue("is_leave_to_absent_convert_count", 10);
            setValue("apply_penalty_count", 1);
          }
        }}
      />
    </Box>

    {watch("is_leave_to_absent_convert") && (
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Apply a leave penalty when an employee&apos;s total leave and
          absence days exceed the configured threshold.
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box sx={{ flex: "1 1 260px" }}>
            <Typography fontWeight="medium" sx={{ mb: 1 }}>
              Threshold (Days) <span style={{ color: themes.redColor }}>*</span>
            </Typography>
            <TextField
              size="small"
              fullWidth
              type="number"
              value={watch("is_leave_to_absent_convert_count")}
              {...register("is_leave_to_absent_convert_count", {
                required: "Enter value",
              })}
              onChange={(event) => {
                const value = event.target.value;
                if (thresholdCountRegex.test(value)) {
                  setValue("is_leave_to_absent_convert_count", value);
                }
              }}
              error={Boolean(errors?.is_leave_to_absent_convert_count)}
              helperText={
                errors?.is_leave_to_absent_convert_count?.message ||
                "When the total leave and absence days in a month exceed this number, the penalty will be applied."
              }
            />
          </Box>

          <Box sx={{ flex: "1 1 260px" }}>
            <Typography fontWeight="medium" sx={{ mb: 1 }}>
              Penalty Value <span style={{ color: themes.redColor }}>*</span>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <TextField
                size="small"
                type="number"
                sx={{ width: 100 }}
                value={watch("apply_penalty_count")}
                {...register("apply_penalty_count", {
                  required: "Enter value",
                })}
                onChange={(event) => {
                  const value = event.target.value;
                  if (penaltyValueRegex.test(value)) {
                    setValue(
                      "apply_penalty_count",
                      value,
                    );
                  }
                }}
                error={Boolean(
                  errors?.apply_penalty_count,
                )}
              />
              <Box
                sx={{
                  border: `1px solid ${themes.miliedLightGray}`,
                  borderRadius: 1,
                  px: 1.5,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#f5f5fd",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography variant="body2">Leave Day(s)</Typography>
              </Box>
            </Box>
            <Typography
              variant="body2"
              color={
                errors?.apply_penalty_count
                  ? "error"
                  : "text.secondary"
              }
              sx={{ mt: 0.5 }}
            >
              {errors?.apply_penalty_count?.message ||
                "Number of leave days to deduct when the threshold is exceeded. (e.g. 1 will create a -1 leave day penalty)"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            border: "1px solid #c7d9f7",
            backgroundColor: "#eef4ff",
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <InfoIcon fontSize="small" sx={{ color: "#1a56db", mt: "2px" }} />
          <Box>
            <Typography fontWeight="medium" sx={{ color: "#1a3a8f" }}>
              Calculation
            </Typography>
            <Typography variant="body2" sx={{ color: "#33488f" }}>
              All leave days (all leave types) and all absence days
              (including sandwich absence) will be counted together against
              the threshold. If the total exceeds the configured threshold,
              the system will automatically apply the specified leave
              penalty.
            </Typography>
          </Box>
        </Box>
      </Box>
    )}
  </Box>
);
